import { useRef, useState, useEffect } from "react";

/**
 * Hook that handles hover functionality for Menu Showit web component
 * @param {Object} menuRef - Ref object for the menu
 * @param {number} hoverDelay - Delay in milliseconds before menu is hidden (default: 1 second)
 * @returns {Object} - Object containing handleMouseEnter and handleMouseLeave functions
 */
const useMenuPopoverHover = (menuRef, hoverDelay = 1000) => {
  const [isActive, setIsActive] = useState(false);
  const closeTimeoutRef = useRef(null);
  const shouldHideRef = useRef(false);

  // Effect to show/hide menu based on active state
  useEffect(() => {
    const menu = menuRef?.current;
    if (!menu) return;

    (async () => {
      await menu.updateComplete;
      if (isActive) {
        menu.showPopover();
      } else if (shouldHideRef.current) {
        menu.hidePopover();
      }
    })();
  }, [menuRef, isActive]);

  // Clean up
  useEffect(
    () => () => {
      if (closeTimeoutRef.current) {
        clearTimeout(closeTimeoutRef.current);
        closeTimeoutRef.current = null;
      }
    },
    []
  );

  return {
    handleMouseEnter: () => {
      shouldHideRef.current = false;
      if (closeTimeoutRef.current) {
        clearTimeout(closeTimeoutRef.current);
        closeTimeoutRef.current = null;
      }
      setIsActive(true);
    },
    handleMouseLeave: () => {
      closeTimeoutRef.current = setTimeout(() => {
        shouldHideRef.current = true;
        setIsActive(false);
      }, hoverDelay);
    },
  };
};

export default useMenuPopoverHover;
